import { Node } from "@tiptap/vue-2";
import { Extension, Mark, mergeAttributes, getMarkRange } from "@tiptap/core";
import { HardBreak } from "@tiptap/extension-hard-break";
import { Document } from "@tiptap/extension-document";
import TableCell from "@tiptap/extension-table-cell";
import TableHeader from "@tiptap/extension-table-header";
import Image from "@tiptap/extension-image";
import { Plugin, PluginKey, TextSelection } from "prosemirror-state";
import { Decoration, DecorationSet } from "prosemirror-view";

function parseMargins(value) {
  if (!value) return { top: 10, right: 10, bottom: 10, left: 10 };
  if (typeof value === 'object' && value !== null && !Array.isArray(value)) {
    return {
      top: parseInt(value.top, 10) || 0,
      right: parseInt(value.right, 10) || 0,
      bottom: parseInt(value.bottom, 10) || 0,
      left: parseInt(value.left, 10) || 0,
    };
  }
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (typeof parsed === 'object' && parsed !== null) {
        return parseMargins(parsed);
      }
    } catch (e) { /* not JSON */ }
    const num = parseInt(value, 10);
    if (!isNaN(num)) return { top: num, right: num, bottom: num, left: num };
  }
  if (typeof value === 'number') {
    return { top: value, right: value, bottom: value, left: value };
  }
  return { top: 10, right: 10, bottom: 10, left: 10 };
}

function loadExtensions() {
  function parseBorderSide(element, side) {
    const val = element.style[`border${side}`];
    if (val && !val.includes('d1d5db') && !val.includes('gray') && !val.includes('grey')) {
      return val;
    }
    const shorthand = element.style.border;
    if (shorthand && !shorthand.includes('d1d5db') && !shorthand.includes('gray') && !shorthand.includes('grey')) {
      return shorthand;
    }
    return "1px solid #000";
  }

  function borderSideAttr(side) {
    return {
      default: "1px solid #000",
      parseHTML: (element) => parseBorderSide(element, side),
      renderHTML: (attributes) => {
        const key = `border${side}`;
        const val = attributes[key];
        const cssProp = `border-${side.toLowerCase()}`;
        return { style: `${cssProp}: ${val}` };
      },
    };
  }

  const CustomTableCell = TableCell.extend({
    addAttributes() {
      return {
        ...this.parent(),

        color: {
          default: "#000",
          parseHTML: (element) => element.style.color || "#000",
          renderHTML: (attributes) => {
            return {
              style: `color: ${attributes.color}`,
            };
          },
        },

        borderTop: borderSideAttr('Top'),
        borderRight: borderSideAttr('Right'),
        borderBottom: borderSideAttr('Bottom'),
        borderLeft: borderSideAttr('Left'),

        backgroundColor: {
          default: null,
          parseHTML: (element) => element.getAttribute("data-background-color"),
          renderHTML: (attributes) => {
            return {
              "data-background-color": attributes.backgroundColor,
              style: `background-color: ${attributes.backgroundColor}`,
            };
          },
        },
        style: {
          default: "",
        },
      };
    },
  });

  const CustomTableHeader = TableHeader.extend({
    addAttributes() {
      return {
        ...this.parent(),

        color: {
          default: "#000",
          parseHTML: (element) => element.style.color || "#000",
          renderHTML: (attributes) => {
            return { style: `color: ${attributes.color}` };
          },
        },

        borderTop: borderSideAttr('Top'),
        borderRight: borderSideAttr('Right'),
        borderBottom: borderSideAttr('Bottom'),
        borderLeft: borderSideAttr('Left'),

        backgroundColor: {
          default: null,
          parseHTML: (element) => element.getAttribute("data-background-color"),
          renderHTML: (attributes) => {
            return {
              "data-background-color": attributes.backgroundColor,
              style: `background-color: ${attributes.backgroundColor}`,
            };
          },
        },
      };
    },
  });

  const CustomTable = Node.create({
    name: "tableCustom",

    isolating: true,

    parseHTML() {
      return [{ tag: "table" }];
    },

    renderHTML({ HTMLAttributes }) {
      return ["table", HTMLAttributes, 0];
    },

    addAttributes() {
      return {
        style: {
          default: "border-collapse: collapse;",
        },
      };
    },
  });

  const HardBreakEnter = HardBreak.extend({
    addKeyboardShortcuts() {
      return {
        //'Enter': () => this.editor.commands.setHardBreak()
      };
    },
  });

  const FontSize = Extension.create({
    name: "fontSize",

    addOptions() {
      return {
        types: ["textStyle"],
      };
    },

    addGlobalAttributes() {
      return [
        {
          types: this.options.types,
          attributes: {
            fontSize: {
              default: null,
              parseHTML: (element) =>
                element.style.fontSize.replace(/['"]+/g, ""),
              renderHTML: (attributes) => {
                if (!attributes.fontSize) {
                  return {};
                }

                return {
                  style: `font-size: ${attributes.fontSize}`,
                };
              },
            },
          },
        },
      ];
    },

    addCommands() {
      return {
        setFontSize:
          (fontSize) =>
          ({ chain }) => {
            return chain().setMark("textStyle", { fontSize }).run();
          },
        unsetFontSize:
          () =>
          ({ chain }) => {
            return chain()
              .setMark("textStyle", { fontSize: null })
              .removeEmptyTextStyle()
              .run();
          },
      };
    },
  });

  const TextSpacing = Extension.create({
    name: 'textSpacing',

    addGlobalAttributes() {
      return [
        {
          types: ['paragraph', 'heading'],
          attributes: {
            lineHeight: {
              default: null,
              parseHTML: (el) => el.style.lineHeight || null,
              renderHTML: (attrs) => {
                if (!attrs.lineHeight) return {};
                return { style: `line-height: ${attrs.lineHeight}` };
              },
            },
            textPadding: {
              default: null,
              parseHTML: (el) => el.getAttribute('data-text-padding') || null,
              renderHTML: (attrs) => {
                if (!attrs.textPadding) return {};
                const p = parseMargins(attrs.textPadding);
                return {
                  'data-text-padding': typeof attrs.textPadding === 'string' ? attrs.textPadding : JSON.stringify(p),
                  style: `padding: ${p.top}px ${p.right}px ${p.bottom}px ${p.left}px`,
                };
              },
            },
          },
        },
      ];
    },

    addCommands() {
      return {
        setLineHeight:
          (lineHeight) =>
          ({ commands }) => {
            return commands.updateAttributes('paragraph', { lineHeight })
              && commands.updateAttributes('heading', { lineHeight });
          },
        unsetLineHeight:
          () =>
          ({ commands }) => {
            return commands.resetAttributes('paragraph', 'lineHeight')
              && commands.resetAttributes('heading', 'lineHeight');
          },
        setTextPadding:
          (padding) =>
          ({ commands }) => {
            const val = JSON.stringify(parseMargins(padding));
            return commands.updateAttributes('paragraph', { textPadding: val })
              && commands.updateAttributes('heading', { textPadding: val });
          },
        unsetTextPadding:
          () =>
          ({ commands }) => {
            return commands.resetAttributes('paragraph', 'textPadding')
              && commands.resetAttributes('heading', 'textPadding');
          },
      };
    },
  });

  const RESERVED_PLACEHOLDERS = ['page', 'pages'];
  const reservedPlaceholderRegex = new RegExp(
    '\\{\\{\\s*\\.(?:' + RESERVED_PLACEHOLDERS.join('|') + ')\\s*\\}\\}', 'g'
  );

  const ReservedPlaceholderHighlight = Extension.create({
    name: 'reservedPlaceholderHighlight',

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey('reservedPlaceholderHighlight'),
          props: {
            decorations(state) {
              const decorations = [];
              state.doc.descendants((node, pos) => {
                if (!node.isText) return;
                if (node.type.name === 'pageNumber') return false;
                const parent = state.doc.resolve(pos).parent;
                if (parent && (parent.type.name === 'pageHeader' || parent.type.name === 'pageFooter')) return;

                let match;
                reservedPlaceholderRegex.lastIndex = 0;
                while ((match = reservedPlaceholderRegex.exec(node.text)) !== null) {
                  const from = pos + match.index;
                  const to = from + match[0].length;
                  decorations.push(
                    Decoration.inline(from, to, {
                      class: 'reserved-placeholder-warning',
                      title: 'Reserved keyword — use "Insert Page Number" instead',
                    })
                  );
                }
              });
              return DecorationSet.create(state.doc, decorations);
            },
          },
        }),
      ];
    },
  });

  const ImageResize = Image.extend({
    addAttributes() {
      return {
        src: {
          default: null,
        },
        alt: {
          default: null,
        },
        style: {
          default: "width: 100%; height: auto; cursor: pointer;",
          parseHTML: (element) => {
            const width = element.getAttribute("width");
            return width
              ? `width: ${width}px; height: auto; cursor: pointer;`
              : `${element.style.cssText}`;
          },
        },
        title: {
          default: null,
        },
        loading: {
          default: null,
        },
        srcset: {
          default: null,
        },
        sizes: {
          default: null,
        },
        crossorigin: {
          default: null,
        },
        usemap: {
          default: null,
        },
        ismap: {
          default: null,
        },
        width: {
          default: null,
        },
        height: {
          default: null,
        },
        referrerpolicy: {
          default: null,
        },
        longdesc: {
          default: null,
        },
        decoding: {
          default: null,
        },
        class: {
          default: null,
        },
        id: {
          default: null,
        },
        name: {
          default: null,
        },
        draggable: {
          default: true,
        },
        tabindex: {
          default: null,
        },
        "aria-label": {
          default: null,
        },
        "aria-labelledby": {
          default: null,
        },
        "aria-describedby": {
          default: null,
        },
      };
    },
    addNodeView() {
      return ({ node, editor, getPos }) => {
        const {
          view,
          options: { editable },
        } = editor;
        const { style } = node.attrs;
        const $wrapper = document.createElement("div");
        const $container = document.createElement("div");
        const $img = document.createElement("img");
        const iconStyle = "width: 24px; height: 24px; cursor: pointer;";

        const dispatchNodeView = () => {
          if (typeof getPos === "function") {
            const newAttrs = {
              ...node.attrs,
              style: `${$img.style.cssText}`,
            };
            view.dispatch(
              view.state.tr.setNodeMarkup(getPos(), null, newAttrs)
            );
          }
        };
        const paintPositionContoller = () => {
          const $postionController = document.createElement("div");

          const $leftController = document.createElement("img");
          const $centerController = document.createElement("img");
          const $rightController = document.createElement("img");

          const controllerMouseOver = (e) => {
            e.target.style.opacity = 0.3;
          };

          const controllerMouseOut = (e) => {
            e.target.style.opacity = 1;
          };

          $postionController.setAttribute(
            "style",
            "position: absolute; top: 0%; left: 50%; width: 100px; height: 25px; z-index: 999; background-color: rgba(255, 255, 255, 0.7); border-radius: 4px; border: 2px solid #6C6C6C; cursor: pointer; transform: translate(-50%, -50%); display: flex; justify-content: space-between; align-items: center; padding: 0 10px;"
          );

          $leftController.setAttribute(
            "src",
            "https://fonts.gstatic.com/s/i/short-term/release/materialsymbolsoutlined/format_align_left/default/20px.svg"
          );
          $leftController.setAttribute("style", iconStyle);
          $leftController.addEventListener("mouseover", controllerMouseOver);
          $leftController.addEventListener("mouseout", controllerMouseOut);

          $centerController.setAttribute(
            "src",
            "https://fonts.gstatic.com/s/i/short-term/release/materialsymbolsoutlined/format_align_center/default/20px.svg"
          );
          $centerController.setAttribute("style", iconStyle);
          $centerController.addEventListener("mouseover", controllerMouseOver);
          $centerController.addEventListener("mouseout", controllerMouseOut);

          $rightController.setAttribute(
            "src",
            "https://fonts.gstatic.com/s/i/short-term/release/materialsymbolsoutlined/format_align_right/default/20px.svg"
          );
          $rightController.setAttribute("style", iconStyle);
          $rightController.addEventListener("mouseover", controllerMouseOver);
          $rightController.addEventListener("mouseout", controllerMouseOut);

          $leftController.addEventListener("click", () => {
            $img.setAttribute(
              "style",
              `${$img.style.cssText} margin: 0 auto 0 0;`
            );
            dispatchNodeView();
          });
          $centerController.addEventListener("click", () => {
            $img.setAttribute("style", `${$img.style.cssText} margin: 0 auto;`);
            dispatchNodeView();
          });
          $rightController.addEventListener("click", () => {
            $img.setAttribute(
              "style",
              `${$img.style.cssText} margin: 0 0 0 auto;`
            );
            dispatchNodeView();
          });

          $postionController.appendChild($leftController);
          $postionController.appendChild($centerController);
          $postionController.appendChild($rightController);

          $container.appendChild($postionController);
        };

        $wrapper.setAttribute("style", `display: flex;`);
        $wrapper.appendChild($container);

        $container.setAttribute("style", `${style}`);
        $container.appendChild($img);

        Object.entries(node.attrs).forEach(([key, value]) => {
          if (value === undefined || value === null) return;
          $img.setAttribute(key, value);
        });

        if (!editable) return { dom: $img };

        const dotsPosition = [
          "top: -4px; left: -4px; cursor: nwse-resize;",
          "top: -4px; right: -4px; cursor: nesw-resize;",
          "bottom: -4px; left: -4px; cursor: nesw-resize;",
          "bottom: -4px; right: -4px; cursor: nwse-resize;",
        ];

        let isResizing = false;
        let startX, startWidth;

        $container.addEventListener("click", () => {
          //remove remaining dots and position controller
          if ($container.childElementCount > 3) {
            for (let i = 0; i < 5; i++) {
              $container.removeChild($container.lastChild);
            }
          }

          paintPositionContoller();

          $container.setAttribute(
            "style",
            `position: relative; border: 1px dashed #6C6C6C; ${style} cursor: pointer;`
          );

          Array.from({ length: 4 }, (_, index) => {
            const $dot = document.createElement("div");
            $dot.setAttribute(
              "style",
              `position: absolute; width: 9px; height: 9px; border: 1.5px solid #6C6C6C; border-radius: 50%; ${dotsPosition[index]}`
            );

            $dot.addEventListener("mousedown", (e) => {
              e.preventDefault();
              isResizing = true;
              startX = e.clientX;
              startWidth = $container.offsetWidth;

              const onMouseMove = (e) => {
                if (!isResizing) return;
                const deltaX =
                  index % 2 === 0 ? -(e.clientX - startX) : e.clientX - startX;

                const newWidth = startWidth + deltaX;

                $container.style.width = newWidth + "px";

                $img.style.width = newWidth + "px";
              };

              const onMouseUp = () => {
                if (isResizing) {
                  isResizing = false;
                }
                dispatchNodeView();

                document.removeEventListener("mousemove", onMouseMove);
                document.removeEventListener("mouseup", onMouseUp);
              };

              document.addEventListener("mousemove", onMouseMove);
              document.addEventListener("mouseup", onMouseUp);
            });
            $container.appendChild($dot);
          });
        });

        document.addEventListener("click", (e) => {
          const $target = e.target;
          const isClickInside =
            $container.contains($target) || $target.style.cssText === iconStyle;

          if (!isClickInside) {
            const containerStyle = $container.getAttribute("style");
            const newStyle = containerStyle.replace(
              "border: 1px dashed #6C6C6C;",
              ""
            );
            $container.setAttribute("style", newStyle);

            if ($container.childElementCount > 3) {
              for (let i = 0; i < 5; i++) {
                $container.removeChild($container.lastChild);
              }
            }
          }
        });

        return {
          dom: $wrapper,
        };
      };
    },
  });

  const MainTagExtension = Node.create({
    name: "main",
    priority: 1000,

    isolating: true,
    group: "main",
    content: "pageHeader? block+ pageFooter?",
    draggable: true,

    parseHTML() {
      return [{ tag: "main" }];
    },

    renderHTML({ HTMLAttributes }) {
      return ["main", HTMLAttributes, 0];
    },

    addAttributes() {
      return {
        class: {
          default: "page",
        },

        margin: {
          default: JSON.stringify({ top: 10, right: 10, bottom: 10, left: 10 }),
          parseHTML: (element) => {
            const raw = element.getAttribute("margin");
            return JSON.stringify(parseMargins(raw));
          },
          renderHTML: (attributes) => {
            const m = parseMargins(attributes.margin);
            let style = `padding:${m.top}px ${m.right}px ${m.bottom}px ${m.left}px;`;
            style += `--page-margin-top:${m.top}px;--page-margin-right:${m.right}px;--page-margin-bottom:${m.bottom}px;--page-margin-left:${m.left}px;`;
            if (!attributes.firstPage) {
              style += "page-break-before:always;";
            }
            return {
              margin: typeof attributes.margin === 'string' ? attributes.margin : JSON.stringify(m),
              style,
            };
          },
        },

        firstPage: {
          default: false,
        },

        style: {
          default: "padding:10px",
          parseHTML: (element) => {
            const raw = element.getAttribute("margin") || '10';
            const m = parseMargins(raw);
            const firstPage = element.getAttribute("firstpage") || false;
            let style = `padding:${m.top}px ${m.right}px ${m.bottom}px ${m.left}px;`;
            style += `--page-margin-top:${m.top}px;--page-margin-right:${m.right}px;--page-margin-bottom:${m.bottom}px;--page-margin-left:${m.left}px;`;
            if (!firstPage) {
              style += "page-break-before:always;";
            }
            return style;
          },
          renderHTML: (attributes) => {
            const m = parseMargins(attributes.margin);
            let style = `padding:${m.top}px ${m.right}px ${m.bottom}px ${m.left}px;`;
            style += `--page-margin-top:${m.top}px;--page-margin-right:${m.right}px;--page-margin-bottom:${m.bottom}px;--page-margin-left:${m.left}px;`;
            if (!attributes.firstPage) {
              style += "page-break-before:always;";
            }
            return { style };
          },
        },
      };
    },

    addCommands() {
      return {
        setDocumentMargins: (margin) => (o) => {
          const m = parseMargins(margin);
          o.chain().updateAttributes("main", {
            margin: JSON.stringify(m),
          }).run();
        },

        setAllDocumentMargins: (margin) => ({ editor }) => {
          const m = parseMargins(margin);
          const marginStr = JSON.stringify(m);
          const { state } = editor;
          const tr = state.tr;

          state.doc.forEach((sectionNode, sectionPos) => {
            if (sectionNode.type.name === "section") {
              sectionNode.forEach((mainNode, mainOffset) => {
                if (mainNode.type.name === "main") {
                  const mainPos = sectionPos + 1 + mainOffset;
                  tr.setNodeMarkup(mainPos, undefined, {
                    ...mainNode.attrs,
                    margin: marginStr,
                  });
                }
              });
            }
          });

          if (tr.docChanged) {
            editor.view.dispatch(tr);
          }

          return true;
        },

        addPage:
          () =>
          ({ editor, commands }) => {
            const { state } = editor;
            const tr = state.tr;
            let insertPos = null;
            let lastSection = null;

            state.doc.forEach((node, pos) => {
              if (node.type.name === "section") {
                insertPos = pos + node.nodeSize - 1;
                lastSection = node;
              }
            });

            if (insertPos !== null) {
              const mainType = state.schema.nodes.main;
              const pType = state.schema.nodes.paragraph;
              const hType = state.schema.nodes.pageHeader;
              const fType = state.schema.nodes.pageFooter;

              let srcHeader = null, srcFooter = null;
              if (lastSection) {
                lastSection.forEach(m => {
                  if (m.type.name === 'main') {
                    m.forEach(c => {
                      if (c.type.name === 'pageHeader' && !srcHeader) srcHeader = c;
                      if (c.type.name === 'pageFooter' && !srcFooter) srcFooter = c;
                    });
                  }
                });
              }

              const children = [];
              if (srcHeader) children.push(srcHeader.copy(srcHeader.content));
              children.push(pType.create());
              if (srcFooter) children.push(srcFooter.copy(srcFooter.content));

              const newPage = mainType.create({ firstPage: false }, children);
              tr.insert(insertPos, newPage);
              editor.view.dispatch(tr);
            }

            commands.calculatePages();
          },

        toggleSectionHeader:
          () =>
          ({ state, dispatch }) => {
            const { $from } = state.selection;
            let sectionPos = null, sectionNode = null;
            for (let d = $from.depth; d >= 0; d--) {
              if ($from.node(d).type.name === 'section') {
                sectionPos = $from.before(d);
                sectionNode = $from.node(d);
                break;
              }
            }
            if (!sectionNode) return false;

            const hType = state.schema.nodes.pageHeader;
            const pType = state.schema.nodes.paragraph;
            const tr = state.tr;

            const pages = [];
            sectionNode.forEach((mainNode, mainOffset) => {
              if (mainNode.type.name === 'main') {
                pages.push({ node: mainNode, offset: mainOffset });
              }
            });

            const hasHeader = pages.length > 0 && pages[0].node.child(0)?.type.name === 'pageHeader';

            for (let i = pages.length - 1; i >= 0; i--) {
              const { node: mainNode, offset: mainOffset } = pages[i];
              const mainPos = sectionPos + 1 + mainOffset;

              if (hasHeader && mainNode.child(0).type.name === 'pageHeader') {
                tr.delete(mainPos + 1, mainPos + 1 + mainNode.child(0).nodeSize);
              } else if (!hasHeader && mainNode.child(0).type.name !== 'pageHeader') {
                tr.insert(mainPos + 1, hType.create(null, pType.create()));
              }
            }

            if (dispatch && tr.docChanged) dispatch(tr);
            return true;
          },

        toggleSectionFooter:
          () =>
          ({ state, dispatch }) => {
            const { $from } = state.selection;
            let sectionPos = null, sectionNode = null;
            for (let d = $from.depth; d >= 0; d--) {
              if ($from.node(d).type.name === 'section') {
                sectionPos = $from.before(d);
                sectionNode = $from.node(d);
                break;
              }
            }
            if (!sectionNode) return false;

            const fType = state.schema.nodes.pageFooter;
            const pType = state.schema.nodes.paragraph;
            const tr = state.tr;

            const pages = [];
            sectionNode.forEach((mainNode, mainOffset) => {
              if (mainNode.type.name === 'main') {
                pages.push({ node: mainNode, offset: mainOffset });
              }
            });

            const hasFooter = pages.length > 0 && pages[0].node.lastChild?.type.name === 'pageFooter';

            for (let i = pages.length - 1; i >= 0; i--) {
              const { node: mainNode, offset: mainOffset } = pages[i];
              const mainPos = sectionPos + 1 + mainOffset;

              if (hasFooter && mainNode.lastChild.type.name === 'pageFooter') {
                const footerStart = mainPos + 1 + mainNode.content.size - mainNode.lastChild.nodeSize;
                tr.delete(footerStart, footerStart + mainNode.lastChild.nodeSize);
              } else if (!hasFooter && mainNode.lastChild.type.name !== 'pageFooter') {
                const endPos = mainPos + 1 + mainNode.content.size;
                tr.insert(endPos, fType.create(null, pType.create()));
              }
            }

            if (dispatch && tr.docChanged) dispatch(tr);
            return true;
          },

        insertPageNumber:
          () =>
          ({ state, dispatch }) => {
            const node = state.schema.nodes.pageNumber.create();
            if (dispatch) dispatch(state.tr.replaceSelectionWith(node));
            return true;
          },

        calculatePages:
          () =>
          ({ editor }) => {
            const { state } = editor;
            const tr = state.tr;
            let isFirstMain = true;

            state.doc.forEach((sectionNode, sectionPos) => {
              if (sectionNode.type.name === "section") {
                sectionNode.forEach((mainNode, mainOffset) => {
                  if (mainNode.type.name === "main") {
                    const mainPos = sectionPos + 1 + mainOffset;
                    tr.setNodeMarkup(mainPos, undefined, {
                      ...mainNode.attrs,
                      firstPage: isFirstMain,
                    });
                    isFirstMain = false;
                  }
                });
              }
            });

            if (tr.docChanged) {
              editor.view.dispatch(tr);
            }

            return true;
          },
      };
    },

    addKeyboardShortcuts() {
      return {
        Backspace: ({ editor }) => {
          const { state } = editor;
          const { $from, empty } = state.selection;

          if (!empty) return false;

          const parentNode = $from.node(-1);
          if (!parentNode || parentNode.type.name !== "main") return false;

          const mainPos = $from.before(-1);

          if (parentNode.content.size <= 2 && mainPos > 0) {
            editor.commands.deleteNode("main");
            editor.commands.calculatePages();
            return true;
          }

          if ($from.parentOffset === 0 && $from.index(-1) === 0 && mainPos > 0) {
            const mainNodes = [];
            state.doc.forEach((sectionNode, sectionOffset) => {
              if (sectionNode.type.name === 'section') {
                sectionNode.forEach((mainNode, mainOffset) => {
                  if (mainNode.type.name === 'main') {
                    mainNodes.push({ node: mainNode, pos: sectionOffset + 1 + mainOffset });
                  }
                });
              }
            });

            const currIdx = mainNodes.findIndex(m => m.pos === mainPos);
            if (currIdx <= 0) return false;

            const prev = mainNodes[currIdx - 1];
            const endOfPrev = prev.pos + 1 + prev.node.content.size;

            try {
              const resolved = state.doc.resolve(endOfPrev);
              const sel = TextSelection.near(resolved, -1);
              editor.view.dispatch(state.tr.setSelection(sel));
            } catch (e) {}

            return true;
          }

          return false;
        },
      };
    },

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey('headerFooterSync'),
          appendTransaction(transactions, oldState, newState) {
            if (!transactions.some(tr => tr.docChanged)) return null;

            const { $from } = newState.selection;
            let activeMainNode = null;
            for (let d = $from.depth; d >= 0; d--) {
              if ($from.node(d).type.name === 'main') {
                activeMainNode = $from.node(d);
                break;
              }
            }
            if (!activeMainNode) return null;

            let srcHeader = null, srcFooter = null;
            activeMainNode.forEach(c => {
              if (c.type.name === 'pageHeader') srcHeader = c;
              if (c.type.name === 'pageFooter') srcFooter = c;
            });
            if (!srcHeader && !srcFooter) return null;

            let activeSectionPos = null;
            for (let d = $from.depth; d >= 0; d--) {
              if ($from.node(d).type.name === 'section') {
                activeSectionPos = $from.before(d);
                break;
              }
            }
            if (activeSectionPos === null) return null;

            const sectionNode = newState.doc.nodeAt(activeSectionPos);
            if (!sectionNode) return null;

            const tr = newState.tr;
            let changed = false;
            const activeMainPos = $from.before($from.depth - (() => {
              for (let d = $from.depth; d >= 0; d--) {
                if ($from.node(d).type.name === 'main') return $from.depth - d;
              }
              return 0;
            })());

            sectionNode.forEach((mainNode, mainOffset) => {
              if (mainNode.type.name !== 'main') return;
              const mainPos = activeSectionPos + 1 + mainOffset;

              mainNode.forEach((child, childOffset) => {
                const childPos = mainPos + 1 + childOffset;
                if (child.type.name === 'pageHeader' && srcHeader) {
                  if (!child.content.eq(srcHeader.content)) {
                    const from = tr.mapping.map(childPos + 1);
                    const to = tr.mapping.map(childPos + 1 + child.content.size);
                    tr.replaceWith(from, to, srcHeader.content);
                    changed = true;
                  }
                  const hAttrsMatch = ['backgroundColor','paddingTop','paddingRight','paddingBottom','paddingLeft']
                    .every(k => child.attrs[k] === srcHeader.attrs[k]);
                  if (!hAttrsMatch) {
                    tr.setNodeMarkup(tr.mapping.map(childPos), undefined, { ...srcHeader.attrs });
                    changed = true;
                  }
                }
                if (child.type.name === 'pageFooter' && srcFooter) {
                  if (!child.content.eq(srcFooter.content)) {
                    const from = tr.mapping.map(childPos + 1);
                    const to = tr.mapping.map(childPos + 1 + child.content.size);
                    tr.replaceWith(from, to, srcFooter.content);
                    changed = true;
                  }
                  const fAttrsMatch = ['backgroundColor','paddingTop','paddingRight','paddingBottom','paddingLeft']
                    .every(k => child.attrs[k] === srcFooter.attrs[k]);
                  if (!fAttrsMatch) {
                    tr.setNodeMarkup(tr.mapping.map(childPos), undefined, { ...srcFooter.attrs });
                    changed = true;
                  }
                }
              });
            });

            return changed ? tr : null;
          },
        }),
      ];
    },
  });

  const PageHeaderNode = Node.create({
    name: 'pageHeader',
    content: 'block+',
    isolating: true,
    defining: true,

    parseHTML() {
      return [{ tag: 'div.page-header-content' }];
    },

    renderHTML({ HTMLAttributes }) {
      const pt = parseInt(HTMLAttributes['data-padding-top']) || 0;
      const pr = parseInt(HTMLAttributes['data-padding-right']) || 0;
      const pb = parseInt(HTMLAttributes['data-padding-bottom']) || 0;
      const pl = parseInt(HTMLAttributes['data-padding-left']) || 0;
      const bg = HTMLAttributes['data-background-color'] || '';
      let style = `padding:${pt}px ${pr}px ${pb}px ${pl}px;`;
      if (bg) style += `background-color:${bg};`;
      return ['div', {
        ...HTMLAttributes,
        class: 'page-header-content',
        style,
      }, 0];
    },

    addAttributes() {
      return {
        paddingTop: {
          default: 0,
          parseHTML: el => parseInt(el.getAttribute('data-padding-top')) || 0,
          renderHTML: attrs => ({ 'data-padding-top': attrs.paddingTop }),
        },
        paddingRight: {
          default: 0,
          parseHTML: el => parseInt(el.getAttribute('data-padding-right')) || 0,
          renderHTML: attrs => ({ 'data-padding-right': attrs.paddingRight }),
        },
        paddingBottom: {
          default: 0,
          parseHTML: el => parseInt(el.getAttribute('data-padding-bottom')) || 0,
          renderHTML: attrs => ({ 'data-padding-bottom': attrs.paddingBottom }),
        },
        paddingLeft: {
          default: 0,
          parseHTML: el => parseInt(el.getAttribute('data-padding-left')) || 0,
          renderHTML: attrs => ({ 'data-padding-left': attrs.paddingLeft }),
        },
        backgroundColor: {
          default: null,
          parseHTML: el => el.getAttribute('data-background-color') || null,
          renderHTML: attrs => {
            if (!attrs.backgroundColor) return {};
            return { 'data-background-color': attrs.backgroundColor };
          },
        },
      };
    },
  });

  const PageFooterNode = Node.create({
    name: 'pageFooter',
    content: 'block+',
    isolating: true,
    defining: true,

    parseHTML() {
      return [{ tag: 'div.page-footer-content' }];
    },

    renderHTML({ HTMLAttributes }) {
      const pt = parseInt(HTMLAttributes['data-padding-top']) || 0;
      const pr = parseInt(HTMLAttributes['data-padding-right']) || 0;
      const pb = parseInt(HTMLAttributes['data-padding-bottom']) || 0;
      const pl = parseInt(HTMLAttributes['data-padding-left']) || 0;
      const bg = HTMLAttributes['data-background-color'] || '';
      let style = `padding:${pt}px ${pr}px ${pb}px ${pl}px;`;
      if (bg) style += `background-color:${bg};`;
      return ['div', {
        ...HTMLAttributes,
        class: 'page-footer-content',
        style,
      }, 0];
    },

    addAttributes() {
      return {
        paddingTop: {
          default: 0,
          parseHTML: el => parseInt(el.getAttribute('data-padding-top')) || 0,
          renderHTML: attrs => ({ 'data-padding-top': attrs.paddingTop }),
        },
        paddingRight: {
          default: 0,
          parseHTML: el => parseInt(el.getAttribute('data-padding-right')) || 0,
          renderHTML: attrs => ({ 'data-padding-right': attrs.paddingRight }),
        },
        paddingBottom: {
          default: 0,
          parseHTML: el => parseInt(el.getAttribute('data-padding-bottom')) || 0,
          renderHTML: attrs => ({ 'data-padding-bottom': attrs.paddingBottom }),
        },
        paddingLeft: {
          default: 0,
          parseHTML: el => parseInt(el.getAttribute('data-padding-left')) || 0,
          renderHTML: attrs => ({ 'data-padding-left': attrs.paddingLeft }),
        },
        backgroundColor: {
          default: null,
          parseHTML: el => el.getAttribute('data-background-color') || null,
          renderHTML: attrs => {
            if (!attrs.backgroundColor) return {};
            return { 'data-background-color': attrs.backgroundColor };
          },
        },
      };
    },
  });

  const PageNumberNode = Node.create({
    name: 'pageNumber',
    group: 'inline',
    inline: true,
    atom: true,

    addAttributes() {
      return {
        format: {
          default: '{{ .page }} / {{ .pages }}',
          parseHTML: el => {
            const raw = el.getAttribute('data-format') || '{{ .page }} / {{ .pages }}';
            return raw.replace(/\{\{page\}\}/g, '{{ .page }}').replace(/\{\{pages\}\}/g, '{{ .pages }}');
          },
          renderHTML: attrs => ({ 'data-format': attrs.format }),
        },
      };
    },

    parseHTML() {
      return [{ tag: 'span.page-number-node' }];
    },

    renderHTML({ HTMLAttributes }) {
      return ['span', { ...HTMLAttributes, class: 'page-number-node' }, '#'];
    },

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey('pageNumberUpdate'),
          view() {
            return {
              update(view) {
                view.dom.querySelectorAll('.page-number-node').forEach(el => {
                  const page = el.closest('main.page');
                  if (!page) return;
                  const pages = view.dom.querySelectorAll('main.page');
                  const idx = Array.from(pages).indexOf(page);
                  const pn = idx + 1;
                  const total = pages.length;
                  const fmt = el.getAttribute('data-format') || '';
                  el.textContent = fmt
                    .replace(/\{\{page\}\}/g, pn)
                    .replace(/\{\{pages\}\}/g, total)
                    .replace(/\{\{\s*\.page\s*\}\}/g, pn)
                    .replace(/\{\{\s*\.pages\s*\}\}/g, total)
                    || (pn + ' / ' + total);
                });
              },
            };
          },
        }),
      ];
    },
  });

  const SectionNode = Node.create({
    name: "section",
    content: "main+",
    isolating: true,
    draggable: false,

    parseHTML() {
      return [{ tag: "section" }];
    },

    renderHTML({ HTMLAttributes }) {
      const attrs = { ...HTMLAttributes };
      const styles = [];
      if (attrs["data-background-image"]) {
        styles.push(`--page-bg-image: url(${attrs["data-background-image"]})`);
      }
      if (styles.length) {
        attrs.style = styles.join(';');
      }
      return ["section", attrs, 0];
    },

    addAttributes() {
      return {
        class: {
          default: "document-section",
        },
        "data-name": {
          default: "Document",
          parseHTML: (element) => element.getAttribute("data-name") || "Document",
        },
        backgroundImage: {
          default: null,
          parseHTML: (element) => element.getAttribute("data-background-image") || null,
          renderHTML: (attributes) => {
            if (!attributes.backgroundImage) return {};
            return { "data-background-image": attributes.backgroundImage };
          },
        },
      };
    },

    addCommands() {
      return {
        addSection:
          (name) =>
          ({ editor }) => {
            const { state } = editor;
            const { selection } = state;
            const $from = selection.$from;
            const tr = state.tr;
            const sectionType = state.schema.nodes.section;
            const mainType = state.schema.nodes.main;
            const pType = state.schema.nodes.paragraph;

            let sectionDepth = -1, pageDepth = -1;
            for (let d = $from.depth; d >= 0; d--) {
              if ($from.node(d).type.name === "main" && pageDepth === -1) pageDepth = d;
              if ($from.node(d).type.name === "section" && sectionDepth === -1) sectionDepth = d;
            }

            const sectionNode = sectionDepth >= 0 ? $from.node(sectionDepth) : null;
            const sectionPos = sectionDepth >= 0 ? $from.before(sectionDepth) : null;
            const pageNode = pageDepth >= 0 ? $from.node(pageDepth) : null;
            const pagePos = pageDepth >= 0 ? $from.before(pageDepth) : null;

            if (!sectionNode || !pageNode) {
              const empty = sectionType.create(
                { "data-name": name || "New Document", class: "document-section" },
                mainType.create({ firstPage: false }, pType.create())
              );
              const pos = state.doc.content.size;
              tr.insert(pos, empty);
              tr.setSelection(TextSelection.create(tr.doc, pos + 3));
              editor.view.dispatch(tr);
              editor.commands.calculatePages();
              editor.commands.focus();
              return true;
            }

            const pageIndex = $from.index(sectionDepth);
            const blockIndex = $from.index(pageDepth);
            const pageContentStart = pagePos + 1;

            let blockOffsetInPage = 0;
            pageNode.forEach((child, offset, idx) => {
              if (idx === blockIndex) blockOffsetInPage = offset;
            });
            const blockContentStart = pageContentStart + blockOffsetInPage + 1;
            const atBlockStart = $from.pos <= blockContentStart;

            const splitBlockIdx = atBlockStart ? blockIndex : blockIndex + 1;

            const blocksFromPage = [];
            pageNode.forEach((child, offset, idx) => {
              if (idx >= splitBlockIdx) blocksFromPage.push(child);
            });

            const pagesAfter = [];
            sectionNode.forEach((child, offset, idx) => {
              if (idx > pageIndex) pagesAfter.push(child);
            });

            if (blocksFromPage.length === 0 && pagesAfter.length === 0) {
              const empty = sectionType.create(
                { "data-name": name || "New Document", class: "document-section" },
                mainType.create({ firstPage: false }, pType.create())
              );
              const pos = sectionPos + sectionNode.nodeSize;
              tr.insert(pos, empty);
              tr.setSelection(TextSelection.create(tr.doc, pos + 3));
              editor.view.dispatch(tr);
              editor.commands.calculatePages();
              editor.commands.focus();
              return true;
            }

            const newPages = [];
            if (blocksFromPage.length > 0) {
              newPages.push(mainType.create({}, blocksFromPage));
            }
            pagesAfter.forEach((p) => newPages.push(p));
            if (newPages.length === 0) {
              newPages.push(mainType.create({}, pType.create()));
            }

            const newSection = sectionType.create(
              { "data-name": name || "New Document", class: "document-section" },
              newPages
            );

            // Delete moved content: pages after current (range B), then blocks from current page (range A)
            if (pagesAfter.length > 0) {
              const bStart = pagePos + pageNode.nodeSize;
              const bEnd = sectionPos + 1 + sectionNode.content.size;
              tr.delete(bStart, bEnd);
            }

            if (blocksFromPage.length > 0) {
              let aStart = null;
              pageNode.forEach((child, childOffset, idx) => {
                if (idx === splitBlockIdx) aStart = pagePos + 1 + childOffset;
              });
              const aEnd = pagePos + 1 + pageNode.content.size;
              if (aStart !== null) {
                tr.delete(tr.mapping.map(aStart), tr.mapping.map(aEnd));
              }
            }

            // If page lost all blocks, add empty paragraph
            if (splitBlockIdx === 0) {
              tr.insert(tr.mapping.map(pagePos + 1), pType.create());
            }

            // Insert new section after current section
            const insertPos = tr.mapping.map(sectionPos + sectionNode.nodeSize);
            tr.insert(insertPos, newSection);

            tr.setSelection(TextSelection.create(tr.doc, insertPos + 3));
            editor.view.dispatch(tr);
            editor.commands.calculatePages();
            editor.commands.focus();
            return true;
          },

        renameSection:
          (sectionIndex, newName) =>
          ({ editor }) => {
            const { state } = editor;
            const tr = state.tr;
            let idx = 0;
            state.doc.forEach((node, pos) => {
              if (node.type.name === "section") {
                if (idx === sectionIndex) {
                  tr.setNodeMarkup(pos, undefined, {
                    ...node.attrs,
                    "data-name": newName,
                  });
                }
                idx++;
              }
            });
            if (tr.docChanged) editor.view.dispatch(tr);
            return true;
          },

        mergeSection:
          (sectionIndex) =>
          ({ editor }) => {
            const { state } = editor;
            const sections = [];
            state.doc.forEach((node, pos) => {
              if (node.type.name === "section") {
                sections.push({ node, pos });
              }
            });
            if (sectionIndex < 1 || sectionIndex >= sections.length) return false;

            const target = sections[sectionIndex - 1];
            const source = sections[sectionIndex];
            const tr = state.tr;

            const pages = [];
            source.node.forEach((child) => {
              if (child.type.name === "main") pages.push(child);
            });

            const insertPos = target.pos + target.node.nodeSize - 1;
            tr.insert(insertPos, pages);

            const sourceStart = tr.mapping.map(source.pos);
            const sourceEnd = tr.mapping.map(source.pos + source.node.nodeSize);
            tr.delete(sourceStart, sourceEnd);

            editor.view.dispatch(tr);
            editor.commands.calculatePages();
            return true;
          },

        deleteSection:
          (sectionIndex) =>
          ({ editor }) => {
            const { state } = editor;
            const sections = [];
            state.doc.forEach((node, pos) => {
              if (node.type.name === "section") {
                sections.push({ node, pos });
              }
            });
            if (sections.length <= 1) return false;
            if (sectionIndex < 0 || sectionIndex >= sections.length) return false;

            const { node, pos } = sections[sectionIndex];
            const tr = state.tr.delete(pos, pos + node.nodeSize);
            editor.view.dispatch(tr);
            editor.commands.calculatePages();
            return true;
          },

        setSectionBackground:
          (backgroundImage) =>
          ({ editor }) => {
            const { state } = editor;
            const { $from } = state.selection;
            for (let d = $from.depth; d >= 0; d--) {
              if ($from.node(d).type.name === 'section') {
                const sectionPos = $from.before(d);
                const tr = state.tr.setNodeMarkup(sectionPos, undefined, {
                  ...$from.node(d).attrs,
                  backgroundImage,
                });
                editor.view.dispatch(tr);
                return true;
              }
            }
            return false;
          },

      };
    },

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey("sectionClickGuard"),
          props: {
            handleClick(view, pos, event) {
              const clickedPage = event.target.closest("main.page");
              if (!clickedPage) return false;
              const clickedSection = clickedPage.closest("section.document-section");
              if (!clickedSection) return false;

              const $pos = view.state.doc.resolve(pos);
              let resolvedSectionDOM = null;
              for (let d = $pos.depth; d >= 0; d--) {
                if ($pos.node(d).type.name === "section") {
                  try {
                    resolvedSectionDOM = view.nodeDOM($pos.before(d));
                  } catch (ignored) { /* */ }
                  break;
                }
              }

              if (resolvedSectionDOM === clickedSection) return false;

              try {
                const pagePos = view.posAtDOM(clickedPage, 0);
                const $pagePos = view.state.doc.resolve(pagePos);
                const sel = TextSelection.findFrom($pagePos, 1, true);
                if (sel) {
                  view.dispatch(view.state.tr.setSelection(sel));
                  return true;
                }
              } catch (ignored) { /* */ }

              return false;
            },
          },
        }),
      ];
    },
  });

  const CustomDocument = Document.extend({
    content: "section+",
  });

  const Comments = Mark.create({
    name: "comments",

    addAttributes() {
      return {
        content: {
          default: null,
          parseHTML: (element) =>
            element.getAttribute("data-comment-content") || "",
          renderHTML: (attributes) => {
            return {
              // write it back out on the <span> as data-comment-content="…"
              "data-comment-content": attributes.content,
            };
          },
        },
        commentId: {
          default: null,
          parseHTML: (element) =>
            element.getAttribute("data-comment-id") || null,
          renderHTML: (attributes) => {
            return {
              "data-comment-id": attributes.commentId,
            };
          },
        },
      };
    },

    // Which tags/styles ProseMirror should treat as this mark
    parseHTML() {
      return [
        {
          tag: "span.protokol-comment",
        },
      ];
    },

    // How to output the mark in the document
    renderHTML({ HTMLAttributes }) {
      return [
        "span",
        mergeAttributes(HTMLAttributes, {
          class: "protokol-comment",
        }),
        0,
      ];
    },

    addCommands() {
      return {
        setComment:
          ({ content }) =>
          ({ commands }) => {
            return commands.toggleMark(this.name, { content });
          },
        removeComment:
          () =>
          ({ state, dispatch }) => {
            const { selection } = state;
            const markType = state.schema.marks[this.name];

            // If the cursor is inside a comment (no explicit selection),
            // expand to the full mark range first.
            if (selection.empty) {
              const range = getMarkRange(selection.$from, markType);
              if (!range) {
                return false; // no comment under cursor
              }

              const tr = state.tr.removeMark(range.from, range.to, markType);
              dispatch(tr);
            } else {
              // If text is selected, fall back to the usual behaviour.
              const { from, to } = selection;
              const tr = state.tr.removeMark(from, to, markType);
              dispatch(tr);
            }
            return true;
          },
        highlightCommentRange:
          ({ commentId, highlight }) =>
          ({ state, dispatch }) => {
            // This will be called from the Vue component to add/remove highlight decoration
            // The actual highlighting is handled by CSS class 'highlighted-comment'
            return true;
          },
      };
    },

    addProseMirrorPlugins() {
      return [
        new Plugin({
          key: new PluginKey("commentDecorations"),
          state: {
            init() {
              return {
                decorations: DecorationSet.empty,
                hoveredCommentId: null,
              };
            },
            apply(tr, value) {
              const meta = tr.getMeta("commentDecorations");
              if (meta) {
                return meta;
              }
              return {
                decorations: value.decorations.map(tr.mapping, tr.doc),
                hoveredCommentId: value.hoveredCommentId,
              };
            },
          },
          props: {
            decorations(state) {
              return this.getState(state).decorations;
            },
          },
        }),
      ];
    },
  });

  const Spinner = Node.create({
    name: "spinner",
    inline: true,
    group: "inline",
    atom: true,

    addAttributes() {
      return {
        id: null,
        class: {
          default: "spinner-border spinner-border-sm align-middle",
        },
      };
    },

    parseHTML() {
      return [{ tag: "span.spinner-border" }];
    },

    renderHTML({ HTMLAttributes }) {
      return ["span", mergeAttributes(HTMLAttributes)];
    },
  });

  return {
    CustomTableCell,
    CustomTableHeader,
    CustomTable,
    HardBreakEnter,
    FontSize,
    ImageResize,
    MainTagExtension,
    SectionNode,
    CustomDocument,
    Comments,
    Spinner,
    PageHeaderNode,
    PageFooterNode,
    PageNumberNode,
    TextSpacing,
    ReservedPlaceholderHighlight,
  };
}

export default loadExtensions;
